<template>
  <div class="kol-kanban">
    <div class="kol-kanban__toolbar">
      <div class="kol-kanban__search">
        <i class="iconoir-search kol-kanban__search-icon"></i>
        <input
          v-model="searchQuery"
          type="text"
          class="kol-kanban__search-input"
          :placeholder="$t('keyoflifeViews.searchPlaceholder')"
        />
        <a
          v-if="searchQuery"
          class="kol-kanban__search-clear"
          @click="searchQuery = ''"
        >
          <i class="iconoir-cancel"></i>
        </a>
      </div>
      <div class="kol-kanban__toolbar-right">
        <span v-if="truncated" class="kol-kanban__notice">
          {{ $t('keyoflifeViews.truncated', { count: rows.length }) }}
        </span>
        <Dropdown
          :value="view.single_select_field"
          :show-search="false"
          :placeholder="$t('keyoflifeViews.chooseStackField')"
          class="kol-kanban__field-dropdown"
          :disabled="readOnly"
          @input="updateStackField($event)"
        >
          <DropdownItem
            v-for="field in singleSelectFields"
            :key="field.id"
            :name="field.name"
            :value="field.id"
            icon="iconoir-view-columns-3"
          ></DropdownItem>
        </Dropdown>
        <Dropdown
          :value="view.color_field"
          :show-search="false"
          :placeholder="$t('keyoflifeViews.chooseColorField')"
          class="kol-kanban__field-dropdown"
          :disabled="readOnly"
          @input="updateColorField($event)"
        >
          <DropdownItem
            :name="$t('keyoflifeViews.noColor')"
            :value="null"
            icon="iconoir-circle"
          ></DropdownItem>
          <DropdownItem
            v-for="field in singleSelectFields"
            :key="field.id"
            :name="field.name"
            :value="field.id"
            icon="iconoir-fill-color"
          ></DropdownItem>
        </Dropdown>
        <a
          class="kol-kanban__nav-button"
          :title="$t('keyoflifeViews.refresh')"
          @click="fetchRows()"
        >
          <i class="iconoir-refresh-double"></i>
        </a>
      </div>
    </div>

    <div v-if="!stackField" class="kol-kanban__empty">
      <i class="iconoir-view-columns-3 kol-kanban__empty-icon"></i>
      <div class="kol-kanban__empty-text">
        {{ $t('keyoflifeViews.noStackFieldTitle') }}
      </div>
      <div class="kol-kanban__empty-subtext">
        {{
          singleSelectFields.length > 0
            ? $t('keyoflifeViews.noStackFieldHelp')
            : $t('keyoflifeViews.noSingleSelectInTable')
        }}
      </div>
      <button
        v-if="singleSelectFields.length === 0 && canCreateField"
        class="button kol-kanban__create-field"
        :class="{ 'button--loading': creatingField }"
        :disabled="creatingField"
        @click="createStatusField()"
      >
        {{ $t('keyoflifeViews.createStatusField') }}
      </button>
    </div>

    <div v-else class="kol-kanban__board">
      <div v-if="loading" class="loading-absolute-center"></div>
      <div
        v-for="column in columns"
        :key="column.key"
        class="kol-kanban__column"
        :class="{
          'kol-kanban__column--drop-target': dragOverColumnKey === column.key,
        }"
        @dragover="onColumnDragOver($event, column)"
        @dragleave="onColumnDragLeave(column)"
        @drop="onColumnDrop(column)"
      >
        <div class="kol-kanban__column-head">
          <span
            v-if="column.color"
            class="kol-kanban__column-dot"
            :class="'background-color--' + column.color"
          ></span>
          <span class="kol-kanban__column-name">{{ column.name }}</span>
          <span class="kol-kanban__column-count">{{
            cardsByColumn[column.key] ? cardsByColumn[column.key].length : 0
          }}</span>
          <a
            v-if="canCreate"
            class="kol-kanban__add"
            :title="$t('keyoflifeViews.addCard')"
            @click="addCard(column)"
          >
            <i class="iconoir-plus"></i>
          </a>
        </div>
        <div class="kol-kanban__column-body">
          <a
            v-for="card in cardsByColumn[column.key] || []"
            :key="card.row.id"
            class="kol-kanban__card"
            :class="[
              {
                'kol-kanban__card--dragging':
                  draggedRow && draggedRow.id === card.row.id,
                'kol-kanban__card--colored': card.color,
              },
              card.color ? 'background-color--' + card.color : '',
            ]"
            :draggable="canManageRows"
            @click="openRow(card.row)"
            @dragstart="onCardDragStart(card.row)"
            @dragend="onCardDragEnd()"
          >
            <div class="kol-kanban__card-title">{{ card.title }}</div>
            <div
              v-for="extra in card.extras"
              :key="extra.fieldId"
              class="kol-kanban__card-extra"
            >
              {{ extra.value }}
            </div>
          </a>
          <a
            v-if="canCreate"
            class="kol-kanban__add-card"
            @click="addCard(column)"
          >
            <i class="iconoir-plus"></i> {{ $t('keyoflifeViews.addCard') }}
          </a>
        </div>
      </div>
    </div>

    <RowEditModal
      ref="rowEditModal"
      :database="database"
      :table="table"
      :view="view"
      :all-fields-in-table="fields"
      :primary-is-sortable="false"
      :visible-fields="fields"
      :hidden-fields="[]"
      :rows="rows"
      :read-only="!canManageRows"
      :show-hidden-fields="false"
      @update="updateValue"
      @refresh-row="refreshRow"
      @field-updated="$emit('refresh', $event)"
      @field-deleted="$emit('refresh')"
      @field-created="$emit('refresh')"
    ></RowEditModal>
  </div>
</template>

<script>
import RowEditModal from '@baserow/modules/database/components/row/RowEditModal'
import RowService from '@baserow/modules/database/services/row'
import { notifyIf } from '@baserow/modules/core/utils/error'

import KanbanService from '@keyoflife-views/services/kanban'

const MAX_CARD_EXTRA_FIELDS = 3
const NO_STACK_KEY = '__none__'

export default {
  name: 'KeyOfLifeKanbanView',
  components: { RowEditModal },
  props: {
    fields: { type: Array, required: true },
    view: { type: Object, required: true },
    table: { type: Object, required: true },
    database: { type: Object, required: true },
    readOnly: { type: Boolean, required: true },
    storePrefix: { type: String, required: false, default: '' },
  },
  data() {
    return {
      rows: [],
      totalCount: 0,
      loading: false,
      searchQuery: '',
      fieldOptions: {},
      draggedRow: null,
      dragOverColumnKey: null,
      creatingField: false,
    }
  },
  computed: {
    stackField() {
      return this.fields.find(
        (field) => field.id === this.view.single_select_field
      )
    },
    colorField() {
      return this.fields.find((field) => field.id === this.view.color_field)
    },
    singleSelectFields() {
      return this.fields.filter((field) => field.type === 'single_select')
    },
    primaryField() {
      return this.fields.find((field) => field.primary)
    },
    cardExtraFields() {
      const excluded = new Set(
        [
          this.primaryField && this.primaryField.id,
          this.view.single_select_field,
          this.view.color_field,
        ].filter((id) => id != null)
      )
      return this.fields
        .filter((field) => !excluded.has(field.id))
        .slice(0, MAX_CARD_EXTRA_FIELDS)
    },
    canManageRows() {
      return (
        !this.readOnly &&
        this.$hasPermission(
          'database.table.update_row',
          this.table,
          this.database.workspace.id
        )
      )
    },
    canCreate() {
      return (
        !this.readOnly &&
        !!this.stackField &&
        this.$hasPermission(
          'database.table.create_row',
          this.table,
          this.database.workspace.id
        )
      )
    },
    canCreateField() {
      return (
        !this.readOnly &&
        this.$hasPermission(
          'database.table.create_field',
          this.table,
          this.database.workspace.id
        )
      )
    },
    truncated() {
      return this.totalCount > this.rows.length
    },
    // Kolumny: "Bez statusu" + opcje pola single-select (w kolejnosci opcji).
    columns() {
      if (!this.stackField) {
        return []
      }
      const options = this.stackField.select_options || []
      return [
        {
          key: NO_STACK_KEY,
          optionId: null,
          name: this.$t('keyoflifeViews.noStack'),
          color: null,
        },
        ...options.map((o) => ({
          key: String(o.id),
          optionId: o.id,
          name: o.value,
          color: o.color,
        })),
      ]
    },
    cardsByColumn() {
      const byColumn = {}
      if (!this.stackField) {
        return byColumn
      }
      const valueKey = `field_${this.stackField.id}`
      const query = this.searchQuery.trim().toLowerCase()
      for (const row of this.rows) {
        const value = row[valueKey]
        const columnKey = value && value.id ? String(value.id) : NO_STACK_KEY
        const title = this.rowTitle(row)
        const extras = this.rowExtras(row)
        if (query) {
          const haystack = [title, ...extras.map((e) => e.value)]
            .join(' ')
            .toLowerCase()
          if (!haystack.includes(query)) {
            continue
          }
        }
        if (!byColumn[columnKey]) {
          byColumn[columnKey] = []
        }
        byColumn[columnKey].push({
          row,
          title,
          extras,
          color: this.rowColor(row),
        })
      }
      return byColumn
    },
  },
  watch: {
    'view.id'() {
      this.fetchRows()
    },
  },
  mounted() {
    this.fetchRows()
  },
  methods: {
    async fetchRows() {
      this.loading = true
      try {
        const { data } = await KanbanService(this.$client).fetchRows({
          viewId: this.view.id,
        })
        this.rows = data.results
        this.totalCount = data.count
        this.fieldOptions = data.field_options || {}
      } catch (error) {
        notifyIf(error, 'rows')
      }
      this.loading = false
    },
    async updateStackField(fieldId) {
      if (fieldId === this.view.single_select_field) {
        return
      }
      try {
        await this.$store.dispatch('view/update', {
          view: this.view,
          values: { single_select_field: fieldId },
        })
      } catch (error) {
        notifyIf(error, 'view')
      }
    },
    async updateColorField(fieldId) {
      if (fieldId === this.view.color_field) {
        return
      }
      try {
        await this.$store.dispatch('view/update', {
          view: this.view,
          values: { color_field: fieldId },
        })
      } catch (error) {
        notifyIf(error, 'view')
      }
    },
    // Tworzy pole single-select "Status" z domyslnymi kolumnami i od razu
    // ustawia je jako pole grupujace widoku (empty-state, gdy tabela go nie ma).
    async createStatusField() {
      if (this.creatingField) {
        return
      }
      this.creatingField = true
      try {
        const { newField } = await this.$store.dispatch('field/create', {
          type: 'single_select',
          table: this.table,
          values: {
            name: this.$t('keyoflifeViews.defaultStatusName'),
            select_options: [
              { value: this.$t('keyoflifeViews.statusTodo'), color: 'light-blue' },
              {
                value: this.$t('keyoflifeViews.statusDoing'),
                color: 'light-orange',
              },
              {
                value: this.$t('keyoflifeViews.statusDone'),
                color: 'light-green',
              },
            ],
          },
        })
        // Powiadom strone tabeli, by przeladowala pola (props `fields`).
        this.$emit('refresh')
        await this.$store.dispatch('view/update', {
          view: this.view,
          values: { single_select_field: newField.id },
        })
        await this.fetchRows()
      } catch (error) {
        notifyIf(error, 'field')
      }
      this.creatingField = false
    },
    openRow(row) {
      this.$refs.rowEditModal.show(row.id)
    },
    // --- Zapis wartosci wiersza (modal @update oraz drag karty) -------------
    async updateRowField(row, field, value) {
      const fieldType = this.$registry.get('field', field.type)
      const prepared = fieldType.prepareValueForUpdate(field, value)
      const payload = { [`field_${field.id}`]: prepared }
      const { data } = await RowService(this.$client).update(
        this.table.id,
        row.id,
        payload
      )
      this.replaceRowLocally(data)
      return data
    },
    async updateValue({ row, field, value }) {
      try {
        await this.updateRowField(row, field, value)
      } catch (error) {
        notifyIf(error, 'field')
      }
    },
    replaceRowLocally(updated) {
      const index = this.rows.findIndex(
        (r) => r !== null && r.id === updated.id
      )
      if (index !== -1) {
        this.rows.splice(index, 1, updated)
      } else {
        this.rows.push(updated)
      }
    },
    async refreshRow(row) {
      try {
        const { data } = await RowService(this.$client).get(
          this.table.id,
          row.id
        )
        this.replaceRowLocally(data)
      } catch (error) {
        notifyIf(error, 'row')
      }
    },
    // --- Drag & drop karty miedzy kolumnami --------------------------------
    onCardDragStart(row) {
      if (!this.canManageRows) {
        return
      }
      this.draggedRow = row
    },
    onCardDragEnd() {
      this.draggedRow = null
      this.dragOverColumnKey = null
    },
    onColumnDragOver(event, column) {
      if (!this.draggedRow) {
        return
      }
      event.preventDefault()
      this.dragOverColumnKey = column.key
    },
    onColumnDragLeave(column) {
      if (this.dragOverColumnKey === column.key) {
        this.dragOverColumnKey = null
      }
    },
    async onColumnDrop(column) {
      const row = this.draggedRow
      this.dragOverColumnKey = null
      this.draggedRow = null
      if (!row || !this.canManageRows) {
        return
      }
      const current = row[`field_${this.stackField.id}`]
      const currentId = current && current.id ? current.id : null
      if (currentId === column.optionId) {
        return
      }
      try {
        await this.updateRowField(row, this.stackField, column.optionId)
      } catch (error) {
        notifyIf(error, 'field')
      }
    },
    // --- Dodanie karty w kolumnie ------------------------------------------
    async addCard(column) {
      if (!this.canCreate) {
        return
      }
      const payload = {}
      if (column.optionId !== null) {
        payload[`field_${this.stackField.id}`] = column.optionId
      }
      try {
        const { data } = await RowService(this.$client).create(
          this.table.id,
          payload
        )
        this.rows.push(data)
        this.totalCount += 1
        this.$nextTick(() => this.$refs.rowEditModal.show(data.id))
      } catch (error) {
        notifyIf(error, 'row')
      }
    },
    // --- Pomocnicze --------------------------------------------------------
    rowTitle(row) {
      const field = this.primaryField
      if (!field) {
        return ''
      }
      const fieldType = this.$registry.get('field', field.type)
      const value = fieldType.toHumanReadableString(
        field,
        row[`field_${field.id}`]
      )
      return value || this.$t('keyoflifeViews.unnamed')
    },
    rowExtras(row) {
      const extras = []
      for (const field of this.cardExtraFields) {
        const fieldType = this.$registry.get('field', field.type)
        const text = fieldType.toHumanReadableString(
          field,
          row[`field_${field.id}`]
        )
        if (text !== null && text !== undefined && String(text).trim() !== '') {
          extras.push({ fieldId: field.id, value: String(text) })
        }
      }
      return extras
    },
    rowColor(row) {
      if (!this.colorField) {
        return null
      }
      const value = row[`field_${this.colorField.id}`]
      return value && value.color ? value.color : null
    },
  },
}
</script>
